/**
 * L'authentification, côté client — les quatre routes de SPEC-API §4.4.
 *
 * | Rôle | Route | Corps |
 * |---|---|---|
 * | Inscription | `POST /api/auth/sign-up/email` | `{ name, email, password }` |
 * | Connexion | `POST /api/auth/sign-in/email` | `{ email, password }` |
 * | Déconnexion | `POST /api/auth/sign-out` | `{}` |
 * | Session | `GET /api/auth/get-session` | — |
 *
 * ⚠️ **`/api/auth/*` est le seul îlot qui ne parle PAS le format d'erreur §5.0** : better-auth
 * garde le sien (§4.4, « format d'erreur de better-auth conservé tel quel »). D'où ce module à
 * part, et son propre décodage — passer ces réponses dans `appelApi` rendrait « le serveur a
 * refusé la requête » là où l'utilisateur a simplement tapé son mot de passe de travers.
 *
 * Le cookie de session (`maxime.session_token`, `HttpOnly`, 30 jours — §4.1) est posé et
 * effacé par le serveur : **rien de tout ça ne transite par du code applicatif**, et le client
 * ne détient jamais le jeton. C'est aussi pour ça qu'on ne stocke aucun mot de passe, nulle
 * part, même le temps d'un rendu.
 */

export interface Utilisateur {
  id: string;
  email: string;
  name: string;
}

/** Un refus de better-auth, traduit. `code` reste brut : c'est lui qui décide, pas la phrase. */
export class ErreurAuth extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = "ErreurAuth";
  }
}

/**
 * Les refus qu'un joueur peut réellement provoquer, en français.
 *
 * Les codes viennent de better-auth ; la liste est **ouverte** (un code inconnu retombe sur le
 * message du serveur), à l'inverse de `game/erreurs.ts` dont l'exhaustivité est garantie par le
 * compilateur. La différence n'est pas un relâchement : les codes du moteur sont un enum que
 * nous possédons, ceux de better-auth appartiennent à une dépendance.
 */
const MESSAGES: Record<string, string> = {
  USER_ALREADY_EXISTS: "Cette adresse a déjà un compte. Connecte-toi plutôt.",
  USER_ALREADY_EXISTS_USE_ANOTHER_EMAIL: "Cette adresse a déjà un compte. Connecte-toi plutôt.",
  INVALID_EMAIL: "Cette adresse e-mail n'a pas l'air valide.",
  INVALID_EMAIL_OR_PASSWORD: "Adresse ou mot de passe incorrect.",
  INVALID_PASSWORD: "Adresse ou mot de passe incorrect.",
  USER_NOT_FOUND: "Adresse ou mot de passe incorrect.",
  PASSWORD_TOO_SHORT: "Mot de passe trop court : 8 caractères au minimum.",
  PASSWORD_TOO_LONG: "Mot de passe trop long : 128 caractères au maximum.",
  FAILED_TO_CREATE_USER: "La création du compte a échoué. Réessaie dans un instant.",
};

/** Longueurs imposées par le serveur (§4.1) — affichées au joueur avant qu'il n'échoue. */
export const MDP_MIN = 8;
export const MDP_MAX = 128;

interface CorpsErreurAuth {
  message?: unknown;
  code?: unknown;
  error?: { message?: unknown; code?: unknown };
}

async function appelAuth<T>(chemin: string, corps?: unknown): Promise<T> {
  let reponse: Response;
  try {
    reponse = await fetch(`/api/auth${chemin}`, {
      method: corps === undefined ? "GET" : "POST",
      credentials: "same-origin",
      headers:
        corps === undefined
          ? { accept: "application/json" }
          : { accept: "application/json", "content-type": "application/json" },
      body: corps === undefined ? undefined : JSON.stringify(corps),
    });
  } catch {
    throw new ErreurAuth(0, "network", "Le serveur ne répond pas. Réessaie dans un instant.");
  }

  const texte = await reponse.text();
  const json: unknown = texte.length > 0 ? safeParse(texte) : null;

  if (!reponse.ok) {
    const brut = (json ?? {}) as CorpsErreurAuth;
    const code =
      typeof brut.code === "string"
        ? brut.code
        : typeof brut.error?.code === "string"
          ? brut.error.code
          : `http_${reponse.status}`;
    // ⚠️ **On ne réaffiche JAMAIS le message brut de better-auth** : il est en anglais
    // (« Invalid origin », « Invalid email or password »), et l'interface est en français
    // (CLAUDE.md). Un code inconnu rend donc une phrase française **et** son code technique —
    // de quoi diagnostiquer sans laisser fuiter de l'anglais dans le jeu.
    const message =
      MESSAGES[code] ?? `Connexion impossible pour le moment (code : ${code}). Réessaie.`;
    throw new ErreurAuth(reponse.status, code, message);
  }

  return json as T;
}

function safeParse(texte: string): unknown {
  try {
    return JSON.parse(texte);
  } catch {
    return null;
  }
}

/** `POST /api/auth/sign-up/email`. Un compte créé est **connecté d'office** (cookie posé). */
export async function sInscrire(input: {
  name: string;
  email: string;
  password: string;
}): Promise<Utilisateur> {
  const r = await appelAuth<{ user?: Utilisateur }>("/sign-up/email", input);
  if (!r?.user) throw new ErreurAuth(500, "no_user", "Compte créé, mais la session n'a pas suivi.");
  return r.user;
}

/** `POST /api/auth/sign-in/email`. */
export async function seConnecter(input: {
  email: string;
  password: string;
}): Promise<Utilisateur> {
  const r = await appelAuth<{ user?: Utilisateur }>("/sign-in/email", input);
  if (!r?.user) throw new ErreurAuth(401, "no_user", "Adresse ou mot de passe incorrect.");
  return r.user;
}

/** `POST /api/auth/sign-out`. Le cookie est invalidé par le serveur. */
export async function seDeconnecter(): Promise<void> {
  await appelAuth<unknown>("/sign-out", {});
}

/**
 * `GET /api/auth/get-session` — `null` quand personne n'est connecté.
 *
 * C'est le **sondage de démarrage** : ne pas avoir de session n'est pas une erreur, et ne doit
 * donc jamais lever. Un serveur injoignable rend `null` lui aussi — l'appelant retombera sur
 * l'écran d'accueil, où le bac à sable reste jouable sans réseau.
 */
export async function sessionCourante(): Promise<Utilisateur | null> {
  try {
    const r = await appelAuth<{ user?: Utilisateur } | null>("/get-session");
    return r?.user ?? null;
  } catch {
    return null;
  }
}
